/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import type { Props } from 'davaux/client/jsx-runtime'
import type { StyleProps } from './types.js'
import { cx, mergeStyle, pickStyleProps } from './types.js'

export interface SpoilerProps extends StyleProps {
  /** Maximum height (px) of the clipped region before the toggle appears. Default: 100. */
  maxHeight?: number
  /** Label for the expand control. Default: 'Show more'. */
  showLabel?: string
  /** Label for the collapse control. Default: 'Show less'. */
  hideLabel?: string
  /** Whether the content starts fully visible. Default: false. */
  initiallyExpanded?: boolean
  /** Allow clicking the content area to expand. Default: false. */
  clickable?: boolean
  /** Blur the clipped content instead of a fade overlay. Pass true for the default (4px) or a pixel number for a custom level. */
  blur?: boolean | number
  class?: string
  style?: string | Record<string, string>
  children?: Props['children']
  [key: string]: unknown
}

function Spoiler({
  maxHeight = 100,
  showLabel = 'Show more',
  hideLabel = 'Show less',
  initiallyExpanded = false,
  clickable = false,
  blur = false,
  class: className,
  style,
  children,
  ...rest
}: SpoilerProps) {
  const [expanded, setExpanded] = createSignal(initiallyExpanded)
  // Default true so SSR always renders the controls (safe fallback).
  // A ResizeObserver corrects this on the client after layout.
  const [overflows, setOverflows] = createSignal(true)
  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)

  const blurActive = blur !== false && blur !== undefined && blur !== 0
  const blurPx = typeof blur === 'number' ? blur : 4

  const contentRef = (el: Element) => {
    const check = () => setOverflows((el as HTMLElement).scrollHeight > maxHeight)
    const observer = new ResizeObserver(check)
    observer.observe(el)
  }

  return (
    <div
      class={cx('dv-spoiler', className)}
      data-expanded={() => expanded() || undefined}
      data-clickable={clickable || undefined}
      data-blur={blurActive || undefined}
      style={mergeStyle({ '--dv-spoiler-max': `${maxHeight}px`, ...(blurActive && { '--dv-spoiler-blur': `${blurPx}px` }), ...styleObj }, style)}
      {...ownRest}
    >
      <div
        class='dv-spoiler-content'
        data-island-slot
        ref={contentRef}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => { if (!expanded()) setExpanded(true) } : undefined}
        onKeyDown={clickable ? (e: KeyboardEvent) => { if (!expanded() && (e.key === 'Enter' || e.key === ' ')) setExpanded(true) } : undefined}
      >
        {children}
      </div>
      {() => (!blur && !expanded() && overflows() ? <div class='dv-spoiler-fade' aria-hidden='true' /> : null)}
      {() => overflows()
        ? (
          <button type='button' class='dv-spoiler-control' onClick={() => setExpanded(!expanded())}>
            {() => (expanded() ? hideLabel : showLabel)}
          </button>
        )
        : null}
    </div>
  )
}

export default reactive(Spoiler)
